import { initialState } from '../context/gameReducer';
import { normalizeNextGameParticipation } from '../domain/nextGameParticipation';
import { BotProfile, EventWithSequence, GameEvent, GameState, Player } from '../types/game';
import { normalizeEventForRpc } from '../types/eventContracts';
import { eventPayloadSchemas } from '../types/eventSchemas';
import { OnlineSnapshot } from '../types/snapshots';
import { isValidGroupGameId } from '../utils/groupGameId';
import { isValidGameId } from '../utils/gameId';
import { logger } from '../utils/logger';
import { makeEmptyDiceMask } from '../domain/gameRules';
import {
  appendEventToOnlineTransport,
  requestLobbyState,
  requestStateSnapshot,
  requestSnapshotEvents,
} from './transport/onlineTransportMirror';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type ValidationResult = { ok: true } | { ok: false; error: string };

function isUuid(value: unknown): boolean {
  return typeof value === 'string' && UUID_REGEX.test(value);
}

function isValidGameIdFormat(gameId: string): boolean {
  return isValidGameId(gameId) || isValidGroupGameId(gameId);
}

function validateEventPayload(event: GameEvent): ValidationResult {
  if (!event || !event.type || !event.timestamp) {
    return { ok: false, error: 'EVENT_INVALID_STRUCTURE' };
  }

  if (!isUuid(event.id)) {
    return { ok: false, error: 'EVENT_INVALID_ID_FORMAT' };
  }

  const payload = event.payload as unknown as Record<string, unknown> | null | undefined;
  if (!payload || typeof payload !== 'object') {
    return { ok: false, error: 'EVENT_INVALID_PAYLOAD' };
  }

  if (!isUuid(payload.eventId)) {
    return { ok: false, error: 'EVENT_INVALID_PAYLOAD_ID' };
  }

  const schema = eventPayloadSchemas[event.type];
  if (!schema) {
    return { ok: true };
  }

  for (const field of schema.required) {
    if (payload[field] == null) {
      return { ok: false, error: `EVENT_MISSING_${field.toUpperCase()}` };
    }
  }

  for (const field of schema.numeric || []) {
    if (payload[field] != null && !Number.isFinite(Number(payload[field]))) {
      return { ok: false, error: `EVENT_INVALID_${field.toUpperCase()}` };
    }
  }

  for (const [field, requiredFields] of Object.entries(schema.arrayOfObjects || {})) {
    const list = payload[field];
    if (!Array.isArray(list)) {
      return { ok: false, error: `EVENT_INVALID_${field.toUpperCase()}` };
    }
    for (const entry of list) {
      for (const required of requiredFields) {
        if ((entry as Record<string, unknown> | undefined)?.[required] == null) {
          return { ok: false, error: `EVENT_INVALID_${field.toUpperCase()}` };
        }
      }
    }
  }

  const uuidFields = ['playerId', 'viewerId', 'newHostPlayerId', 'overtakerId'];
  for (const field of uuidFields) {
    if (payload[field] != null && !isUuid(payload[field])) {
      return { ok: false, error: `EVENT_INVALID_${field.toUpperCase()}` };
    }
  }
  if (Array.isArray(payload.penalties)) {
    for (const penalty of payload.penalties as Array<Record<string, unknown>>) {
      if (penalty?.playerId != null && !isUuid(penalty.playerId)) {
        return { ok: false, error: 'EVENT_INVALID_PENALTY_PLAYER_ID' };
      }
    }
  }

  return { ok: true };
}

function parseSequenceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function readSequenceNumber(event: GameEvent): number | null {
  return parseSequenceNumber((event as { sequenceNumber?: unknown }).sequenceNumber);
}

function withNormalizedPayloadId(event: GameEvent): GameEvent {
  return normalizeEventForRpc(event);
}

function createLobbySeedPlayer(
  input: {
    id: string;
    name: string;
    role: 'player' | 'viewer';
    status: 'active' | 'left';
    isHost: boolean;
    isBot?: boolean;
    botProfile?: BotProfile;
  },
  position: number,
): Player {
  return {
    id: input.id,
    name: input.name,
    score: 0,
    position,
    status: input.status,
    role: input.role,
    isHost: input.isHost,
    isBot: input.isBot === true,
    botProfile: input.botProfile,
    hasFirstEntry: false,
    isWinner: false,
    statistics: {
      totalTurns: 0,
      totalPasses: 0,
      totalNegativePoints: 0,
      averageScore: 0,
      totalOvertakes: 0,
      totalPointsDelta: 0,
    },
  };
}

export interface EventStore {
  appendEvent(gameId: string, event: GameEvent): Promise<{
    success: boolean;
    sequenceNumber?: number;
    acknowledgedEventId?: string;
    warning?: string;
    error?: string;
    errorCode?: string;
    errorDetails?: string | null;
    errorHint?: string | null;
    snapshot?: OnlineSnapshot | null;
  }>;
  getEvents(gameId: string, fromSequence?: number): Promise<EventWithSequence[]>;
  getGameState(gameId: string): Promise<GameState>;
  getSnapshot(gameId: string): Promise<OnlineSnapshot | null>;
}

class PartyKitEventStore implements EventStore {
  async appendEvent(gameId: string, event: GameEvent): Promise<{
    success: boolean;
    sequenceNumber?: number;
    acknowledgedEventId?: string;
    warning?: string;
    error?: string;
    errorCode?: string;
    errorDetails?: string | null;
    errorHint?: string | null;
    snapshot?: OnlineSnapshot | null;
  }> {
    try {
      if (!isValidGameIdFormat(gameId)) {
        return {
          success: false,
          error: 'Invalid game ID format',
          errorCode: 'INVALID_GAME_ID',
        };
      }

      let normalized: GameEvent;
      try {
        normalized = withNormalizedPayloadId(event);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'EVENT_INVALID_PAYLOAD';
        logger.warn('EVENT_STORE_NORMALIZATION_FAILED', {
          gameId,
          eventType: event.type,
          error: message,
        });
        return {
          success: false,
          error: message,
          errorCode: 'INVALID_EVENT_PAYLOAD',
        };
      }
      const validation = validateEventPayload(normalized);
      if (!validation.ok) {
        logger.warn('EVENT_STORE_VALIDATION_FAILED', {
          gameId,
          eventType: normalized.type,
          error: validation.error,
        });
        return {
          success: false,
          error: validation.error,
          errorCode: 'INVALID_EVENT_PAYLOAD',
        };
      }

      const mirrored = await appendEventToOnlineTransport(gameId, normalized);
      if (!mirrored.success) {
        return {
          success: false,
          error: mirrored.error ?? 'PartyKit transport unavailable',
          errorCode: mirrored.errorCode ?? 'PARTYKIT_UNAVAILABLE',
          errorDetails: mirrored.errorDetails,
          errorHint: mirrored.errorHint,
        };
      }

      return {
        success: true,
        acknowledgedEventId: mirrored.acknowledgedEventId ?? normalized.id,
        sequenceNumber: mirrored.sequenceNumber,
        warning: mirrored.warning,
        snapshot: mirrored.snapshot,
      };
    } catch (error) {
      logger.error('EVENT_STORE_APPEND_EXCEPTION', {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        errorCode: 'PARTYKIT_APPEND_FAILED',
      };
    }
  }

  async getEvents(gameId: string, fromSequence: number = 1): Promise<EventWithSequence[]> {
    try {
      if (!isValidGameIdFormat(gameId)) return [];
      const normalizedFrom = Number.isFinite(fromSequence) ? Math.max(1, Math.floor(fromSequence)) : 1;
      const sinceSequence = normalizedFrom - 1;

      const rawEvents = await requestSnapshotEvents(gameId, sinceSequence);
      if (!rawEvents || rawEvents.length === 0) return [];

      const enriched = rawEvents.map((event, index) => {
        const normalized = normalizeEventForRpc(event);
        const sequenceNumber = readSequenceNumber(event) ?? (sinceSequence + index + 1);
        return {
          ...normalized,
          sequenceNumber,
        } as EventWithSequence;
      });

      const byId = new Map<string, EventWithSequence>();
      for (const event of enriched) {
        const existing = byId.get(event.id);
        if (!existing || event.sequenceNumber > existing.sequenceNumber) {
          byId.set(event.id, event);
        }
      }

      return Array.from(byId.values())
        .filter((event) => event.sequenceNumber >= normalizedFrom)
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
    } catch (error) {
      logger.error('EVENT_STORE_FETCH_EXCEPTION', error);
      return [];
    }
  }

  async getGameState(gameId: string): Promise<GameState> {
    try {
      const [snapshot, lobby] = await Promise.all([
        this.getSnapshot(gameId),
        requestLobbyState(gameId),
      ]);

      if (!snapshot && !lobby) {
        return {
          ...initialState,
          id: gameId,
          mode: 'REAL',
        };
      }

      const lobbyPlayers = lobby?.players || [];
      const seedPlayers = lobbyPlayers.length > 0
        ? lobbyPlayers.map((player, index) => createLobbySeedPlayer(player, index + 1))
        : Object.keys(snapshot?.scores || {}).map((id, index) => createLobbySeedPlayer({
            id,
            name: `Player ${index + 1}`,
            role: 'player',
            status: 'active',
            isHost: index === 0,
            isBot: false,
          }, index + 1));

      const firstEntries = snapshot?.firstEntries || {};
      const playerStatuses = snapshot?.playerStatuses || {};
      const players = seedPlayers.map((player) => ({
        ...player,
        score: snapshot?.scores?.[player.id] ?? player.score,
        hasFirstEntry: firstEntries[player.id] ?? player.hasFirstEntry,
        status: playerStatuses[player.id] ?? player.status,
      }));

      const activePlayerId = snapshot?.activePlayerId || lobby?.activePlayerId || null;
      const currentPlayerIndex = activePlayerId
        ? Math.max(0, players.findIndex((player) => player.id === activePlayerId))
        : 0;

      const lobbyState = lobby
        ? {
            phase: lobby.phase,
            readyMap: { ...lobby.readyMap },
            nextGameParticipation: normalizeNextGameParticipation(lobby.nextGameParticipation),
            ...(lobby.order && lobby.order.length > 0 ? { order: [...lobby.order] } : {}),
          }
        : (snapshot?.lobby || { phase: 'LOBBY', readyMap: {}, nextGameParticipation: {} });

      return {
        ...initialState,
        id: snapshot?.gameId || lobby?.gameId || gameId,
        mode: 'ONLINE',
        roomId: snapshot?.roomId || lobby?.roomId || null,
        currentRoundId: snapshot?.currentRoundId || lobby?.currentRoundId || null,
        turnNumber: snapshot?.turn || 1,
        players,
        currentPlayerIndex,
        lobby: lobbyState,
        turnCycle: snapshot?.turnCycle || (activePlayerId
          ? {
              activePlayerId,
              rollCount: snapshot?.rollIndex || 0,
              dice: [],
              kept: makeEmptyDiceMask(),
              capturedMask: makeEmptyDiceMask(),
              status: 'IDLE',
              diceOrigins: [],
            }
          : undefined),
        settings: snapshot?.settings || initialState.settings,
        rapidTurnMeta: snapshot?.rapidTurnMeta ?? null,
        spectators: snapshot?.spectators || [],
        firstEntries,
        events: [],
      } as GameState;
    } catch (error) {
      logger.error('EVENT_STORE_STATE_EXCEPTION', error);
      return {
        ...initialState,
        id: gameId,
        mode: 'REAL',
      };
    }
  }

  async getSnapshot(gameId: string): Promise<OnlineSnapshot | null> {
    try {
      const directSnapshot = await requestStateSnapshot(gameId);
      if (directSnapshot) {
        return directSnapshot;
      }
      return null;
    } catch (error) {
      logger.error('EVENT_STORE_SNAPSHOT_EXCEPTION', { gameId, error });
      return null;
    }
  }
}

const partyKitEventStore = new PartyKitEventStore();
export const eventStore: EventStore = partyKitEventStore;
